import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';

import type { IColumn, TTheme } from '@feathr/components';
import { Pill, TableColumnHeader, Text, Time } from '@feathr/components';
import { TimeFormat } from '@feathr/hooks';

import type { ISampleCreative } from '../sampleData';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';
import * as shellStyles from '../Shell/PrototypeShell.css';

export type TCreativeRowAction = 'preview' | 'archive' | 'restore' | 'delete';

interface ICreativeColumnsArgs {
  base: string;
  onAction: (creative: ISampleCreative, action: TCreativeRowAction) => void;
  renderActions: (
    creative: ISampleCreative,
    onAction: (c: ISampleCreative, a: TCreativeRowAction) => void,
  ) => JSX.Element;
  t: TFunction;
}

const CATEGORY_THEME: Record<ISampleCreative['category'], TTheme> = {
  Display: 'sky',
  Meta: 'cyan',
};

function creativeColumns({
  base,
  onAction,
  renderActions,
  t,
}: ICreativeColumnsArgs): Array<IColumn<ISampleCreative>> {
  return [
    {
      accessor: 'name',
      id: 'name',
      Header: TableColumnHeader({ title: t('Name'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      minWidth: 280,
      Cell({ original }): JSX.Element {
        return <Link to={base}>{original.name}</Link>;
      },
    },
    {
      accessor: 'category',
      id: 'category',
      Header: TableColumnHeader({ title: t('Category'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      width: 110,
      Cell({ original }): JSX.Element {
        return <Pill theme={CATEGORY_THEME[original.category]}>{original.category}</Pill>;
      },
    },
    {
      accessor: 'creativeType',
      id: 'type',
      Header: TableColumnHeader({ title: t('Type'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      width: 160,
      Cell({ original }): JSX.Element {
        return <Text variant={'smallRegular'}>{original.creativeType}</Text>;
      },
    },
    {
      accessor: (row): number => row.attachedCampaignIds.length,
      id: 'campaigns',
      Header: TableColumnHeader({ title: t('Used in'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      width: 120,
      Cell({ original }): JSX.Element {
        const count = original.attachedCampaignIds.length;
        if (count === 0) {
          return <Text variant={'smallRegular'}>{t('—')}</Text>;
        }
        return <Text variant={'smallRegular'}>{t('{{count}} campaigns', { count })}</Text>;
      },
    },
    {
      accessor: 'dateCreated',
      id: 'dateCreated',
      Header: TableColumnHeader({ title: t('Created'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellCenter,
      sortable: true,
      width: 140,
      Cell({ original }): JSX.Element {
        return <Time format={TimeFormat.shortDate} timestamp={original.dateCreated} />;
      },
    },
    {
      id: 'actions',
      Header: TableColumnHeader({ title: '' }),
      width: 56,
      sortable: false,
      className: `${tableStyles.cellCenter} ${shellStyles.actionsCell}`,
      Cell({ original }): JSX.Element {
        return renderActions(original, onAction);
      },
    },
  ];
}

export default creativeColumns;
